import * as React from "react";
import { Button, EmptyState, cn } from "@nexus/ui";
import { TASK_NAVIGATION_SECTION } from "@nexus/contracts";
import { useTranslation } from "react-i18next";

import { createDefaultPersonaSetName } from "../../../lib/persona-opportunity-form.js";
import { PERSONA_OPPORTUNITIES_LAYOUT_ID } from "../../../lib/persona-opportunity-stage-chrome.js";
import type {
  CreatePersonaSetFormValues,
  PersonaOpportunitySet,
  PersonaOpportunityStatusFilter,
  PersonaOpportunityTabKey,
  PersonaOpportunityViewMode,
} from "../../../types/persona-opportunities.types.js";
import type {
  PersonaOpportunitySetDetail,
  RegeneratePersonaOpportunitySetMode,
} from "../../../api/persona-opportunity-sets.api.js";
import { PersonaOpportunitiesEmptyState } from "../PersonaOpportunitiesEmptyState/PersonaOpportunitiesEmptyState.js";
import { PersonaOpportunitySetWorkspace } from "../../workspace/PersonaOpportunitySetWorkspace/PersonaOpportunitySetWorkspace.js";
import { CreatePersonaSetDialog } from "../../sets/CreatePersonaSetDialog/CreatePersonaSetDialog.js";
import { DeletePersonaOpportunitySetDialog } from "../../sets/DeletePersonaOpportunitySetDialog/DeletePersonaOpportunitySetDialog.js";
import { RegeneratePersonaOpportunitySetDialog } from "../../sets/RegeneratePersonaOpportunitySetDialog/RegeneratePersonaOpportunitySetDialog.js";
import { PersonaOpportunitySetList } from "../../sets/PersonaOpportunitySetList/PersonaOpportunitySetList.js";

export interface PersonaOpportunitiesPageProps {
  tenantId: string;
  sets: PersonaOpportunitySet[];
  selectedSet: PersonaOpportunitySet | null;
  detail?: PersonaOpportunitySetDetail;
  isDetailLoading?: boolean;
  canListSets?: boolean;
  canCreateSet?: boolean;
  canUseQueryTuning?: boolean;
  isCreatingSet?: boolean;
  isDeletingSet?: boolean;
  isRegeneratingSet?: boolean;
  openCreateSetOnMount?: boolean;
  onCreateSetDialogOpened?: () => void;
  onSetChange: (set: PersonaOpportunitySet | null) => void;
  onOpenCreateSet?: () => void;
  onGenerateSet?: (values: CreatePersonaSetFormValues) => void;
  onStopSet?: (set: PersonaOpportunitySet) => void;
  onDeleteSet?: (set: PersonaOpportunitySet) => void;
  onRetrySet?: (set: PersonaOpportunitySet) => void;
  onRegenerateSet?: (input: {
    set: PersonaOpportunitySet;
    mode: RegeneratePersonaOpportunitySetMode;
    name?: string;
  }) => void;
  activeTab?: PersonaOpportunityTabKey;
  onTabChange?: (tab: PersonaOpportunityTabKey) => void;
  view?: PersonaOpportunityViewMode;
  onViewChange?: (view: PersonaOpportunityViewMode) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  statusFilter?: PersonaOpportunityStatusFilter;
  onStatusFilterChange?: (value: PersonaOpportunityStatusFilter) => void;
}

export function PersonaOpportunitiesPage({
  tenantId,
  sets,
  selectedSet,
  detail,
  isDetailLoading,
  canListSets = true,
  canCreateSet = true,
  canUseQueryTuning = false,
  isCreatingSet,
  isDeletingSet,
  isRegeneratingSet,
  openCreateSetOnMount,
  onCreateSetDialogOpened,
  onSetChange,
  onOpenCreateSet,
  onGenerateSet,
  onStopSet,
  onDeleteSet,
  onRetrySet,
  onRegenerateSet,
  activeTab = "opportunities",
  onTabChange,
  view = "table",
  onViewChange,
  search = "",
  onSearchChange,
  statusFilter = "all",
  onStatusFilterChange,
}: PersonaOpportunitiesPageProps) {
  const { t } = useTranslation();
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [pendingDefaultName, setPendingDefaultName] = React.useState(() => createDefaultPersonaSetName());
  const [deleteTarget, setDeleteTarget] = React.useState<PersonaOpportunitySet | null>(null);
  const [regenerateTarget, setRegenerateTarget] = React.useState<PersonaOpportunitySet | null>(null);

  const openCreateDialog = React.useCallback(() => {
    setPendingDefaultName(createDefaultPersonaSetName());
    setCreateDialogOpen(true);
    onOpenCreateSet?.();
  }, [onOpenCreateSet]);

  const openCreateDialogRef = React.useRef(openCreateDialog);
  openCreateDialogRef.current = openCreateDialog;

  const didAutoOpenCreateRef = React.useRef(false);
  React.useEffect(() => {
    if (!openCreateSetOnMount || didAutoOpenCreateRef.current) return;
    if (!canCreateSet || !onGenerateSet || selectedSet) return;
    didAutoOpenCreateRef.current = true;
    openCreateDialogRef.current();
    onCreateSetDialogOpened?.();
  }, [openCreateSetOnMount, canCreateSet, onGenerateSet, selectedSet, onCreateSetDialogOpened]);

  const showSetsOverview = canListSets && !selectedSet;

  return (
    <div className="flex min-h-0 flex-1 flex-col transition-all duration-300 ease-in-out">
      <div
        id={PERSONA_OPPORTUNITIES_LAYOUT_ID}
        className={cn(
          "relative mx-auto flex min-h-0 w-full max-w-[1680px] flex-1 flex-col gap-7 overflow-hidden px-8 pt-7",
          showSetsOverview ? "pb-16" : "pb-0",
        )}
      >
        {showSetsOverview && (
          <div className="flex shrink-0 items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-foreground">
                {t("personaOpportunities.title")}
              </h1>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {t(
                  "personaOpportunities.subtitle",
                  "Scale your creative machine with persona-driven opportunity sets.",
                )}
              </p>
            </div>
            {canCreateSet && onGenerateSet && (
              <Button
                type="button"
                size="default"
                iconStart={isCreatingSet ? undefined : "plus"}
                disabled={!!isCreatingSet}
                onClick={openCreateDialog}
                className="shrink-0"
              >
                {isCreatingSet
                  ? t("common.creating")
                  : t("personaOpportunities.newSet")}
              </Button>
            )}
          </div>
        )}

        {selectedSet ? (
          <PersonaOpportunitySetWorkspace
            selectedSet={selectedSet}
            sets={sets}
            detail={detail}
            isDetailLoading={isDetailLoading}
            activeTab={activeTab}
            view={view}
            canCreateSet={canCreateSet && !!onGenerateSet}
            onTabChange={onTabChange ?? (() => undefined)}
            onViewChange={onViewChange ?? (() => undefined)}
            onBack={() => onSetChange(null)}
            onSelectSet={onSetChange}
            onCreateSet={canCreateSet && onGenerateSet ? openCreateDialog : undefined}
            onRegenerate={
              canCreateSet && onRegenerateSet && selectedSet.status === "READY"
                ? () => setRegenerateTarget(selectedSet)
                : undefined
            }
            onRetrySet={canCreateSet ? onRetrySet : undefined}
            search={search}
            onSearchChange={onSearchChange ?? (() => undefined)}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange ?? (() => undefined)}
          />
        ) : (
          <div
            data-task-section={TASK_NAVIGATION_SECTION.PERSONA_OPPORTUNITY_SETS}
            className="min-h-0 min-w-0 flex-1 scroll-mt-6 overflow-y-auto overflow-x-visible"
          >
            {!canListSets ? (
              <EmptyState
                icon="lock"
                title={t("personaOpportunities.noListPermissionTitle")}
                description={t("personaOpportunities.noListPermissionDescription")}
              />
            ) : sets.length === 0 ? (
              <PersonaOpportunitiesEmptyState
                canCreateSet={canCreateSet}
                isCreatingSet={isCreatingSet}
                onCreateSet={canCreateSet && onGenerateSet ? openCreateDialog : undefined}
              />
            ) : (
              <PersonaOpportunitySetList
                sets={sets}
                onSelectSet={onSetChange}
                canMutate={canCreateSet}
                onStopSet={canCreateSet ? onStopSet : undefined}
                onDeleteSet={
                  canCreateSet && onDeleteSet
                    ? (set) => setDeleteTarget(set)
                    : undefined
                }
                onRetrySet={canCreateSet ? onRetrySet : undefined}
                onRegenerateSet={
                  canCreateSet && onRegenerateSet
                    ? (set) => setRegenerateTarget(set)
                    : undefined
                }
              />
            )}
          </div>
        )}
      </div>

      <CreatePersonaSetDialog
        tenantId={tenantId}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        initialName={pendingDefaultName}
        isSubmitting={isCreatingSet}
        canUseQueryTuning={canUseQueryTuning}
        onSubmit={(values) => {
          setCreateDialogOpen(false);
          onGenerateSet?.(values);
        }}
      />

      <DeletePersonaOpportunitySetDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        setName={deleteTarget?.name ?? ""}
        isDeleting={isDeletingSet}
        onConfirm={() => {
          if (!deleteTarget) return;
          onDeleteSet?.(deleteTarget);
          setDeleteTarget(null);
        }}
      />

      <RegeneratePersonaOpportunitySetDialog
        open={regenerateTarget != null}
        onOpenChange={(open) => {
          if (!open) setRegenerateTarget(null);
        }}
        sourceName={regenerateTarget?.name ?? ""}
        isSubmitting={isRegeneratingSet}
        onConfirm={({ mode, name }) => {
          if (!regenerateTarget) return;
          onRegenerateSet?.({ set: regenerateTarget, mode, name });
          setRegenerateTarget(null);
        }}
      />
    </div>
  );
}
